import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Checkbox,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { Metric } from '../../types';

interface OffTrackMetricsModalProps {
  isOpen: boolean;
  onClose: () => void;
  metrics: Metric[];
  onAddMetrics: (metricIds: string[]) => void;
  onSkip: () => void;
}

export function OffTrackMetricsModal({
  isOpen,
  onClose,
  metrics,
  onAddMetrics,
  onSkip,
}: OffTrackMetricsModalProps) {
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(new Set());

  const toggleMetric = (metricId: string) => {
    const newSelected = new Set(selectedMetrics);
    if (newSelected.has(metricId)) {
      newSelected.delete(metricId);
    } else {
      newSelected.add(metricId);
    }
    setSelectedMetrics(newSelected);
  };

  const handleAddSelected = () => {
    onAddMetrics(Array.from(selectedMetrics));
    setSelectedMetrics(new Set());
    onClose();
  };

  const handleSkip = () => {
    setSelectedMetrics(new Set());
    onSkip();
    onClose();
  };

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.800');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Off-Track Metrics Detected</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text color="gray.700">
              The following metrics are currently off-track. Select any you'd like to
              discuss during this L10 meeting:
            </Text>

            {metrics.length === 0 ? (
              <Text color="gray.500" textAlign="center" py={4}>
                No off-track metrics found
              </Text>
            ) : (
              <Box maxH="96" overflowY="auto">
                <VStack spacing={2} align="stretch">
                  {metrics.map((metric) => (
                    <Box
                      key={metric.id}
                      p={3}
                      borderWidth={1}
                      borderColor={borderColor}
                      borderRadius="md"
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                      onClick={() => toggleMetric(metric.id)}
                    >
                      <Checkbox
                        isChecked={selectedMetrics.has(metric.id)}
                        onChange={() => toggleMetric(metric.id)}
                        colorScheme="blue"
                      >
                        <VStack align="start" spacing={1} ml={2}>
                          <Text fontWeight="medium">{metric.metricName || 'Unknown Metric'}</Text>
                          <Text fontSize="sm" color="gray.600">
                            Measurable: {metric.measurable}
                          </Text>
                        </VStack>
                      </Checkbox>
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleSkip}>
            Skip
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleAddSelected}
            isDisabled={selectedMetrics.size === 0}
          >
            Add Selected & Start ({selectedMetrics.size})
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

